import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  FiBox,
  FiCheckCircle,
  FiDollarSign,
  FiEdit,
  FiEye,
  FiPackage,
  FiPlusCircle,
  FiRefreshCcw,
  FiXCircle,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import customerService from '../services/customerService';
import inventoryService from '../services/inventoryService';
import b2bOrderService from '../services/b2bOrderService';
import { toApiDateValue, toDisplayDateValue } from '../utils/dateUtils';

const currency = (value) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(value || 0));

const compactFieldClass = 'w-full rounded-md border-gray-300 px-2 py-1.5 text-sm shadow-sm';
const formatStock = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-AU') : 'N/A';
const resolveOnHandStock = (value) => {
  if (!value) return null;
  const candidate = value.stock ?? value.quantity;
  return Number.isFinite(Number(candidate)) ? Number(candidate) : null;
};
const resolveReservedStock = (value) => {
  if (!value) return 0;
  const candidate = value.reservedStock ?? value.reservedQuantity;
  return Number.isFinite(Number(candidate)) ? Number(candidate) : 0;
};
const resolveAvailableStock = (value) => {
  if (!value) return null;
  if (Number.isFinite(Number(value.availableStock))) {
    return Number(value.availableStock);
  }
  const onHand = resolveOnHandStock(value);
  if (onHand === null) return null;
  return Math.max(0, onHand - resolveReservedStock(value));
};

const roundCurrency = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const statusBadgeClasses = {
  Draft: 'bg-gray-100 text-gray-700',
  Confirmed: 'bg-blue-100 text-blue-700',
  Picking: 'bg-indigo-100 text-indigo-700',
  Packed: 'bg-amber-100 text-amber-800',
  PartiallyFulfilled: 'bg-orange-100 text-orange-700',
  Fulfilled: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
};

const baseLine = {
  productId: '',
  productLabel: '',
  productQuery: '',
  productSku: '',
  productBarcode: '',
  currentStock: null,
  gstApplicable: true,
  orderedQty: 1,
  unitPrice: 0,
  discountType: 'amount',
  discountValue: 0,
};

const normalizeDiscount = (discountType = 'amount', discountValue = 0) => ({
  type: discountType === 'percent' ? 'percent' : 'amount',
  value: Math.max(0, Number(discountValue || 0)),
});

const calculateDiscountAmount = (baseAmount, discountType = 'amount', discountValue = 0) => {
  const normalized = normalizeDiscount(discountType, discountValue);
  if (!baseAmount || normalized.value <= 0) {
    return 0;
  }
  if (normalized.type === 'percent') {
    return roundCurrency(baseAmount * (normalized.value / 100));
  }
  return roundCurrency(Math.min(baseAmount, normalized.value));
};

const buildProductDisplayName = (product) => {
  if (!product) return '';
  return product.name || '';
};

const buildOrderPreview = (lines = [], orderDiscountType = 'amount', orderDiscountValue = 0) => {
  const normalizedLines = [];
  let subtotalBeforeDiscount = 0;
  let lineDiscountTotal = 0;
  let subtotalAfterLineDiscount = 0;

  for (const line of lines) {
    const orderedQty = Math.max(0, Number(line.orderedQty || 0));
    const unitPrice = Math.max(0, Number(line.unitPrice || 0));
    const grossAmount = roundCurrency(orderedQty * unitPrice);
    const lineDiscountAmount = calculateDiscountAmount(grossAmount, line.discountType, line.discountValue);
    const amountAfterLineDiscount = roundCurrency(grossAmount - lineDiscountAmount);

    subtotalBeforeDiscount += grossAmount;
    lineDiscountTotal += lineDiscountAmount;
    subtotalAfterLineDiscount += amountAfterLineDiscount;

    normalizedLines.push({
      ...line,
      orderedQty,
      unitPrice,
      grossAmount,
      lineDiscountAmount,
      amountAfterLineDiscount,
      orderDiscountShare: 0,
      netAmount: amountAfterLineDiscount,
      taxAmount: 0,
      totalAmount: amountAfterLineDiscount,
    });
  }

  const orderDiscountAmount = calculateDiscountAmount(
    subtotalAfterLineDiscount,
    orderDiscountType,
    orderDiscountValue
  );

  let distributedOrderDiscount = 0;
  let taxTotal = 0;
  normalizedLines.forEach((line, index) => {
    const isLast = index === normalizedLines.length - 1;
    let orderDiscountShare = 0;
    if (orderDiscountAmount > 0 && subtotalAfterLineDiscount > 0) {
      if (isLast) {
        orderDiscountShare = roundCurrency(orderDiscountAmount - distributedOrderDiscount);
      } else {
        orderDiscountShare = roundCurrency(
          orderDiscountAmount * (line.amountAfterLineDiscount / subtotalAfterLineDiscount)
        );
        distributedOrderDiscount += orderDiscountShare;
      }
    }
    const netAmount = roundCurrency(line.amountAfterLineDiscount - orderDiscountShare);
    const taxAmount = line.gstApplicable !== false ? roundCurrency(netAmount * 0.1) : 0;
    const totalAmount = roundCurrency(netAmount + taxAmount);

    line.orderDiscountShare = orderDiscountShare;
    line.netAmount = netAmount;
    line.taxAmount = taxAmount;
    line.totalAmount = totalAmount;
    taxTotal += taxAmount;
  });

  const subtotalAfterDiscount = roundCurrency(subtotalAfterLineDiscount - orderDiscountAmount);
  const grandTotal = roundCurrency(subtotalAfterDiscount + taxTotal);

  return {
    lines: normalizedLines,
    subtotalBeforeDiscount: roundCurrency(subtotalBeforeDiscount),
    lineDiscountTotal: roundCurrency(lineDiscountTotal),
    orderDiscountTotal: roundCurrency(orderDiscountAmount),
    subtotalAfterDiscount,
    taxTotal: roundCurrency(taxTotal),
    grandTotal,
  };
};

const ModalShell = ({ title, children, onClose, wide = false, inline = false }) => {
  if (inline) {
    return (
      <div className={`w-full ${wide ? '' : 'max-w-3xl'} rounded-xl bg-white shadow`}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900">
            <FiXCircle size={18} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full ${wide ? 'max-w-6xl' : 'max-w-3xl'} rounded-xl bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900">
            <FiXCircle size={20} />
          </button>
        </div>
        <div className="max-h-[85vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const parseProductLookupSearch = (rawInput) => {
  const trimmed = typeof rawInput === 'string' ? rawInput.trim() : '';
  if (!trimmed) {
    return { term: '', searchMode: undefined, isRegexMode: false };
  }
  if (trimmed.toLowerCase().startsWith('re:')) {
    const term = trimmed.slice(3).trim();
    return { term, searchMode: term ? 'regex' : undefined, isRegexMode: true };
  }
  if (trimmed.startsWith('/') && trimmed.lastIndexOf('/') > 0) {
    return { term: trimmed, searchMode: 'regex', isRegexMode: true };
  }
  return { term: trimmed, searchMode: undefined, isRegexMode: false };
};

const ProductLookupField = ({ storeId, line, disabled, onSearchChange, onSelectProduct }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const resultRefs = useRef([]);
  const searchValue = line.productQuery || '';

  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      left: rect.left,
      top: rect.bottom + 6,
      width: rect.width,
    });
  }, []);

  const runSearch = useCallback(
    async (term) => {
      const parsedSearch = parseProductLookupSearch(term);
      if (!storeId || !parsedSearch.term || disabled) {
        setSearchResults([]);
        setHighlightedIndex(-1);
        return [];
      }
      try {
        const results = await inventoryService.searchProducts(storeId, parsedSearch.term, {
          searchMode: parsedSearch.searchMode,
          lookupContext: 'b2b-order',
        });
        const normalizedResults = Array.isArray(results) ? results : [];
        setSearchResults(normalizedResults);
        setHighlightedIndex(normalizedResults.length ? 0 : -1);
        return normalizedResults;
      } catch (error) {
        setSearchResults([]);
        setHighlightedIndex(-1);
        return [];
      }
    },
    [disabled, storeId]
  );

  useEffect(() => {
    const parsedSearch = parseProductLookupSearch(searchValue);
    if (!parsedSearch.term || disabled || !storeId) {
      setSearchResults([]);
      setHighlightedIndex(-1);
      return;
    }
    if (line.productId && parsedSearch.term === (line.productLabel || '').trim()) {
      setSearchResults([]);
      setHighlightedIndex(-1);
      return;
    }

    const debounce = setTimeout(() => {
      runSearch(searchValue);
    }, 250);

    return () => clearTimeout(debounce);
  }, [disabled, line.productId, line.productLabel, runSearch, searchValue, storeId]);

  const handleSelect = (product) => {
    onSelectProduct(product);
    setSearchResults([]);
    setDropdownStyle(null);
    setHighlightedIndex(-1);
  };

  const resolveScannerSelection = async () => {
    const parsedSearch = parseProductLookupSearch(searchValue);
    if (!parsedSearch.term) return;

    let results = searchResults;
    if (!results.length) {
      results = await runSearch(searchValue);
    }
    if (parsedSearch.isRegexMode) {
      if (results.length === 1) {
        handleSelect(results[0]);
      }
      return;
    }
    const normalized = parsedSearch.term.toLowerCase();
    const exactMatch = results.find(
      (product) =>
        product.barcode?.toLowerCase() === normalized ||
        product.sku?.toLowerCase() === normalized ||
        product.name?.toLowerCase() === normalized
    );
    if (exactMatch) {
      handleSelect(exactMatch);
      return;
    }
    if (results.length === 1) {
      handleSelect(results[0]);
    }
  };

  useEffect(() => {
    if (!searchResults.length) {
      setDropdownStyle(null);
      setHighlightedIndex(-1);
      return undefined;
    }

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [searchResults.length, updateDropdownPosition]);

  useEffect(() => {
    resultRefs.current = [];
  }, [searchResults]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const target = resultRefs.current[highlightedIndex];
    if (!target) return;
    target.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={searchValue}
        onChange={(event) => onSearchChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            if (!searchResults.length) return;
            event.preventDefault();
            setHighlightedIndex((current) => {
              if (current < 0) return 0;
              return Math.min(searchResults.length - 1, current + 1);
            });
            return;
          }
          if (event.key === 'ArrowUp') {
            if (!searchResults.length) return;
            event.preventDefault();
            setHighlightedIndex((current) => {
              if (current < 0) return 0;
              return Math.max(0, current - 1);
            });
            return;
          }
          if (event.key === 'Escape') {
            setSearchResults([]);
            setDropdownStyle(null);
            setHighlightedIndex(-1);
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
              handleSelect(searchResults[highlightedIndex]);
              return;
            }
            resolveScannerSelection();
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            setSearchResults([]);
            setDropdownStyle(null);
            setHighlightedIndex(-1);
          }, 150);
        }}
        onFocus={() => {
          if (searchResults.length) {
            updateDropdownPosition();
            if (highlightedIndex < 0) {
              setHighlightedIndex(0);
            }
          }
        }}
        placeholder="Type name/SKU/barcode, or use re:<pattern> for regex"
        disabled={disabled}
        className={compactFieldClass}
      />
      {searchResults.length > 0 && dropdownStyle && createPortal(
        <div
          className="max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
          style={{
            position: 'fixed',
            left: dropdownStyle.left,
            top: dropdownStyle.top,
            width: dropdownStyle.width,
            zIndex: 9999,
          }}
        >
          {searchResults.map((product, index) => (
            <button
              type="button"
              key={product._id}
              onClick={() => handleSelect(product)}
              onMouseEnter={() => setHighlightedIndex(index)}
              ref={(element) => {
                resultRefs.current[index] = element;
              }}
              className={`w-full border-b px-2 py-1.5 text-left last:border-b-0 ${
                highlightedIndex >= 0 &&
                searchResults[highlightedIndex] &&
                String(searchResults[highlightedIndex]._id) === String(product._id)
                  ? 'bg-primary/10'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="font-medium text-gray-900">{product.name}</div>
              <div className="text-xs text-gray-500">
                SKU: {product.sku || '—'} • Barcode: {product.barcode || '—'} • Available: {formatStock(resolveAvailableStock(product))} • On hand: {formatStock(resolveOnHandStock(product))}
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const B2BOrderFormModal = ({
  open,
  order,
  activeStoreId,
  customers,
  customersLoading,
  customerSearch,
  setCustomerSearch,
  onCustomerSearch,
  canManageCustomers,
  onCreateCustomer,
  error,
  onClose,
  onSave,
  inline = false,
}) => {
  const canEditExisting = !order?._id || ['Draft', 'Confirmed'].includes(order.status);
  const lineRefs = useRef([]);
  const [form, setForm] = useState({
    customerId: '',
    customerLabel: '',
    status: 'Confirmed',
    fulfillmentType: 'pickup',
    customerReference: '',
    requestedDate: '',
    expectedFulfillmentDate: '',
    notes: '',
    orderDiscountType: 'amount',
    orderDiscountValue: 0,
    lines: [{ ...baseLine }],
  });
  const [pendingNewLineIndex, setPendingNewLineIndex] = useState(null);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [createCustomerSaving, setCreateCustomerSaving] = useState(false);
  const [createCustomerError, setCreateCustomerError] = useState('');
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (!open) return;
    lineRefs.current = [];
    setPendingNewLineIndex(null);
    setCreateCustomerOpen(false);
    setCreateCustomerSaving(false);
    setCreateCustomerError('');
    setNewCustomer({
      firstName: '',
      lastName: '',
      businessName: '',
      email: '',
      phone: '',
    });
    if (order?._id) {
      setForm({
        customerId: order.customer?._id || '',
        customerLabel:
          order.customer?.b2bProfile?.businessName ||
          `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim(),
        status: order.status || 'Confirmed',
        fulfillmentType: order.fulfillmentType || 'pickup',
        customerReference: order.customerReference || '',
        requestedDate: toApiDateValue(order.requestedDate),
        expectedFulfillmentDate: toApiDateValue(order.expectedFulfillmentDate),
        notes: order.notes || '',
        orderDiscountType: order.orderDiscount?.type || 'amount',
        orderDiscountValue: order.orderDiscount?.value || 0,
        lines:
          order.lines?.map((line) => ({
            productId: line.product?._id || line.product || '',
            productLabel: line.name || buildProductDisplayName(line.product) || '',
            productQuery: line.name || buildProductDisplayName(line.product) || '',
            productSku: line.sku || line.product?.sku || '',
            productBarcode: line.barcode || line.product?.barcode || '',
            currentStock: resolveAvailableStock(line.product),
            gstApplicable: line.gstApplicable !== false,
            orderedQty: line.orderedQty || 1,
            unitPrice: line.unitPrice || 0,
            discountType: line.lineDiscount?.type || 'amount',
            discountValue: line.lineDiscount?.value || 0,
          })) || [{ ...baseLine }],
      });
      setCustomerSearch('');
    } else {
      setForm({
        customerId: '',
        customerLabel: '',
        status: 'Confirmed',
        fulfillmentType: 'pickup',
        customerReference: '',
        requestedDate: '',
        expectedFulfillmentDate: '',
        notes: '',
        orderDiscountType: 'amount',
        orderDiscountValue: 0,
        lines: [{ ...baseLine }],
      });
    }
  }, [open, order, setCustomerSearch]);

  useEffect(() => {
    if (pendingNewLineIndex === null) return;
    const target = lineRefs.current[pendingNewLineIndex];
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'end' });
    const focusTarget =
      target.querySelector('input[type="text"]') ||
      target.querySelector('input[type="number"]') ||
      target.querySelector('select');
    focusTarget?.focus();
    setPendingNewLineIndex(null);
  }, [form.lines.length, pendingNewLineIndex]);

  const handleLineChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line
      ),
    }));
  };

  const handleProductSearchChange = (index, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const isSameAsSelected = value.trim() === (line.productLabel || '').trim();
        if (isSameAsSelected) {
          return { ...line, productQuery: value };
        }
        return {
          ...line,
          productId: '',
          productLabel: '',
          productQuery: value,
          productSku: '',
          productBarcode: '',
          currentStock: null,
          gstApplicable: true,
        };
      }),
    }));
  };

  const handleSelectProduct = (index, product) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index
          ? {
              ...line,
              productId: product._id,
              productLabel: buildProductDisplayName(product),
              productQuery: buildProductDisplayName(product),
              productSku: product.sku || '',
              productBarcode: product.barcode || '',
              currentStock: resolveAvailableStock(product),
              gstApplicable: product.gstApplicable !== false,
              unitPrice:
                Number(line.unitPrice || 0) > 0 ? Number(line.unitPrice || 0) : Number(product.price || 0),
            }
          : line
      ),
    }));
  };

  const handleAddLine = () => {
    setForm((current) => {
      const nextIndex = current.lines.length;
      setPendingNewLineIndex(nextIndex);
      return { ...current, lines: [...current.lines, { ...baseLine }] };
    });
  };

  const handleRemoveLine = (index) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.length === 1 ? current.lines : current.lines.filter((_, idx) => idx !== index),
    }));
  };

  const handleSelectCustomer = (customer) => {
    setForm((current) => ({
      ...current,
      customerId: customer._id,
      customerLabel:
        customer.b2bProfile?.businessName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
    }));
    setCustomerSearch(
      customer.b2bProfile?.businessName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
    );
  };

  const openCreateCustomer = () => {
    setCreateCustomerOpen(true);
    setCreateCustomerError('');
    setNewCustomer({
      firstName: '',
      lastName: '',
      businessName: customerSearch?.trim() || '',
      email: '',
      phone: '',
    });
  };

  const submitNewCustomer = async () => {
    if (!onCreateCustomer || createCustomerSaving) return;

    const payload = {
      firstName: newCustomer.firstName.trim(),
      lastName: newCustomer.lastName.trim(),
      email: newCustomer.email.trim() || undefined,
      phone: newCustomer.phone.trim() || undefined,
      customerType: 'b2b',
      b2bProfile: {
        businessName: newCustomer.businessName.trim() || undefined,
      },
      storeId: activeStoreId,
      createLoyaltyCard: false,
    };

    setCreateCustomerSaving(true);
    setCreateCustomerError('');
    try {
      const createdCustomer = await onCreateCustomer(payload);
      if (createdCustomer?._id) {
        handleSelectCustomer(createdCustomer);
      }
      setCreateCustomerOpen(false);
    } catch (createError) {
      setCreateCustomerError(
        createError?.response?.data?.message || createError?.message || 'Unable to create customer.'
      );
    } finally {
      setCreateCustomerSaving(false);
    }
  };

  const preview = useMemo(
    () => buildOrderPreview(form.lines, form.orderDiscountType, form.orderDiscountValue),
    [form.lines, form.orderDiscountType, form.orderDiscountValue]
  );

  const isSaveDisabled =
    !form.customerId ||
    form.lines.length === 0 ||
    form.lines.some(
      (line) =>
        !line.productId || Number(line.orderedQty || 0) <= 0 || Number(line.unitPrice || 0) < 0
    );

  const submit = async (event) => {
    event.preventDefault();
    if (isSaveDisabled) {
      return;
    }
    await onSave({
      customer: form.customerId,
      store: activeStoreId,
      status: form.status,
      fulfillmentType: form.fulfillmentType,
      customerReference: form.customerReference,
      requestedDate: form.requestedDate || undefined,
      expectedFulfillmentDate: form.expectedFulfillmentDate || undefined,
      notes: form.notes,
      orderDiscount: {
        type: form.orderDiscountType,
        value: Number(form.orderDiscountValue || 0),
      },
      lines: form.lines.map((line) => ({
        product: line.productId,
        orderedQty: Number(line.orderedQty || 0),
        unitPrice: Number(line.unitPrice || 0),
        lineDiscount: {
          type: line.discountType,
          value: Number(line.discountValue || 0),
        },
      })),
    });
  };

  if (!open) return null;

  return (
    <ModalShell
      title={order?._id ? `B2B Order ${order.orderNumber || ''}`.trim() : 'New B2B Sales Order'}
      onClose={onClose}
      wide
      inline={inline}
    >
      <form onSubmit={submit} className="space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {!canEditExisting && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            This order is no longer editable because it has moved beyond Draft or Confirmed.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Customer Search</label>
            <input
              type="text"
              value={customerSearch}
              onChange={(event) => {
                setCustomerSearch(event.target.value);
                onCustomerSearch(event.target.value);
              }}
              disabled={!canEditExisting && !!order?._id}
              placeholder="Search business customer by name, phone, email, or ID"
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
            {customersLoading && <p className="mt-1 text-xs text-gray-500">Searching customers…</p>}
            {!!customerSearch && customers.length > 0 && canEditExisting && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white">
                {customers.map((customer) => (
                  <button
                    type="button"
                    key={customer._id}
                    onClick={() => handleSelectCustomer(customer)}
                    className="flex w-full flex-col border-b px-3 py-2 text-left hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900">
                      {customer.b2bProfile?.businessName || `${customer.firstName} ${customer.lastName}`}
                    </span>
                    <span className="text-xs text-gray-500">
                      {customer.customerId} · {customer.email || customer.phone || 'No contact'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {!!customerSearch && !customersLoading && customers.length === 0 && canEditExisting && (
              <div className="mt-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                <p className="text-gray-600">No matching customer found.</p>
                {canManageCustomers ? (
                  <button
                    type="button"
                    onClick={openCreateCustomer}
                    className="mt-2 inline-flex items-center rounded-md border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
                  >
                    <FiPlusCircle className="mr-1.5" />
                    Create Customer
                  </button>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">You do not have permission to create customers.</p>
                )}
              </div>
            )}
            {form.customerLabel && (
              <p className="mt-2 text-sm text-emerald-700">Selected customer: {form.customerLabel}</p>
            )}
            {createCustomerOpen && canEditExisting && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">Create New Customer</p>
                  <button
                    type="button"
                    onClick={() => {
                      setCreateCustomerOpen(false);
                      setCreateCustomerError('');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Close
                  </button>
                </div>
                {createCustomerError && (
                  <p className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    {createCustomerError}
                  </p>
                )}
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <input
                      required
                      value={newCustomer.firstName}
                      onChange={(event) =>
                        setNewCustomer((current) => ({ ...current, firstName: event.target.value }))
                      }
                      placeholder="First Name*"
                      className="rounded-md border-gray-300 px-2 py-1.5 text-sm"
                    />
                    <input
                      required
                      value={newCustomer.lastName}
                      onChange={(event) =>
                        setNewCustomer((current) => ({ ...current, lastName: event.target.value }))
                      }
                      placeholder="Last Name*"
                      className="rounded-md border-gray-300 px-2 py-1.5 text-sm"
                    />
                    <input
                      value={newCustomer.businessName}
                      onChange={(event) =>
                        setNewCustomer((current) => ({ ...current, businessName: event.target.value }))
                      }
                      placeholder="Business Name"
                      className="rounded-md border-gray-300 px-2 py-1.5 text-sm sm:col-span-2"
                    />
                    <input
                      type="email"
                      value={newCustomer.email}
                      onChange={(event) =>
                        setNewCustomer((current) => ({ ...current, email: event.target.value }))
                      }
                      placeholder="Email"
                      className="rounded-md border-gray-300 px-2 py-1.5 text-sm"
                    />
                    <input
                      value={newCustomer.phone}
                      onChange={(event) =>
                        setNewCustomer((current) => ({ ...current, phone: event.target.value }))
                      }
                      placeholder="Phone"
                      className="rounded-md border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={submitNewCustomer}
                      disabled={createCustomerSaving}
                      className="inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-dark disabled:opacity-50"
                    >
                      <FiPlusCircle className="mr-1.5" />
                      {createCustomerSaving ? 'Creating...' : 'Create and Select'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Initial Status</label>
              <select
                value={form.status}
                onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                disabled={!canEditExisting}
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="Draft">Draft</option>
                <option value="Confirmed">Confirmed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fulfillment Type</label>
              <select
                value={form.fulfillmentType}
                onChange={(event) => setForm((current) => ({ ...current, fulfillmentType: event.target.value }))}
                disabled={!canEditExisting}
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Requested Date</label>
              <input
                type="date"
                value={form.requestedDate}
                onChange={(event) => setForm((current) => ({ ...current, requestedDate: event.target.value }))}
                disabled={!canEditExisting}
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Expected Pickup / Delivery Date</label>
              <input
                type="date"
                value={form.expectedFulfillmentDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, expectedFulfillmentDate: event.target.value }))
                }
                disabled={!canEditExisting}
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Customer Reference / PO Number</label>
              <input
                type="text"
                value={form.customerReference}
                onChange={(event) =>
                  setForm((current) => ({ ...current, customerReference: event.target.value }))
                }
                disabled={!canEditExisting}
                placeholder="Optional customer PO or internal reference"
                className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Order Discount</label>
                <select
                  value={form.orderDiscountType}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, orderDiscountType: event.target.value }))
                  }
                  disabled={!canEditExisting}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="amount">Amount</option>
                  <option value="percent">Percent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Discount Value</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.orderDiscountValue}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, orderDiscountValue: event.target.value }))
                  }
                  disabled={!canEditExisting}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Order Lines</h3>
            {canEditExisting && (
              <button
                type="button"
                onClick={handleAddLine}
                className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FiPlusCircle className="mr-2" />
                Add Line
              </button>
            )}
          </div>

          <div className="overflow-x-auto overflow-y-visible rounded-lg border border-gray-200 bg-white">
            <div className="min-w-[1180px]">
              <div className="grid grid-cols-[minmax(280px,340px)_86px_64px_110px_130px_110px_90px] gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <div>Product</div>
                <div className="text-right">Available</div>
                <div className="text-right">Qty</div>
                <div className="text-right">Unit Price</div>
                <div>Line Discount</div>
                <div className="text-right">Discount Value</div>
                <div>Action</div>
              </div>

              <div className="divide-y divide-gray-200">
                {preview.lines.map((line, index) => (
                  <div
                    key={`line-${index}`}
                    ref={(element) => {
                      lineRefs.current[index] = element;
                    }}
                    className="px-2 py-1.5"
                  >
                    <div className="grid grid-cols-[minmax(280px,340px)_86px_64px_110px_130px_110px_90px] items-center gap-1">
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="w-8 shrink-0 text-right text-[11px] font-semibold text-gray-500">
                            #{index + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <ProductLookupField
                              storeId={activeStoreId}
                              line={line}
                              disabled={!canEditExisting}
                              onSearchChange={(value) => handleProductSearchChange(index, value)}
                              onSelectProduct={(product) => handleSelectProduct(index, product)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="text-right text-xs font-semibold text-slate-700">
                        {formatStock(line.currentStock)}
                      </div>
                      <div>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={line.orderedQty}
                          onChange={(event) => handleLineChange(index, 'orderedQty', event.target.value)}
                          disabled={!canEditExisting}
                          className={`${compactFieldClass} text-right`}
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unitPrice}
                          onChange={(event) => handleLineChange(index, 'unitPrice', event.target.value)}
                          disabled={!canEditExisting}
                          className={`${compactFieldClass} text-right`}
                        />
                      </div>
                      <div>
                        <select
                          value={line.discountType}
                          onChange={(event) => handleLineChange(index, 'discountType', event.target.value)}
                          disabled={!canEditExisting}
                          className={compactFieldClass}
                        >
                          <option value="amount">Amount</option>
                          <option value="percent">Percent</option>
                        </select>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.discountValue}
                          onChange={(event) => handleLineChange(index, 'discountValue', event.target.value)}
                          disabled={!canEditExisting}
                          className={`${compactFieldClass} text-right`}
                        />
                      </div>
                      <div>
                        {canEditExisting ? (
                          <button
                            type="button"
                            onClick={() => handleRemoveLine(index)}
                            className="rounded-md border border-red-200 px-2 py-1.5 text-[11px] text-red-600 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-500">Locked</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-1 rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-[11px] leading-4 text-gray-700">
                      Before {currency(line.grossAmount)} | Discount -{currency(line.lineDiscountAmount + line.orderDiscountShare)} | After {currency(line.netAmount)} | Tax {currency(line.taxAmount)} | Final {currency(line.totalAmount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {canEditExisting && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={handleAddLine}
                className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <FiPlusCircle className="mr-2" />
                Add Another Line
              </button>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
            <span className="text-sm text-gray-500">Live preview before save</span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subtotal</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{currency(preview.subtotalBeforeDiscount)}</p>
            </div>
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Line Discounts</p>
              <p className="mt-1 text-lg font-semibold text-amber-700">-{currency(preview.lineDiscountTotal)}</p>
            </div>
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Order Discount</p>
              <p className="mt-1 text-lg font-semibold text-amber-700">-{currency(preview.orderDiscountTotal)}</p>
            </div>
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ex Tax</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{currency(preview.subtotalAfterDiscount)}</p>
            </div>
            <div className="rounded-lg bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">GST / Tax</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{currency(preview.taxTotal)}</p>
            </div>
            <div className="rounded-lg bg-slate-900 p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">Grand Total</p>
              <p className="mt-1 text-lg font-semibold text-white">{currency(preview.grandTotal)}</p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            rows="3"
            value={form.notes}
            onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            disabled={!canEditExisting}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>

        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          {canEditExisting && (
            <button
              type="submit"
              disabled={isSaveDisabled}
              className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {order?._id ? 'Save Changes' : 'Create Order'}
            </button>
          )}
        </div>
      </form>
    </ModalShell>
  );
};

const FulfillmentModal = ({ open, order, error, canCreateInvoices, onClose, onSubmit }) => {
  const [quantities, setQuantities] = useState({});
  const [autoCreateInvoice, setAutoCreateInvoice] = useState(true);

  useEffect(() => {
    if (!open || !order) return;
    const nextQuantities = {};
    order.lines?.forEach((line) => {
      const remaining = Math.max(0, Number(line.orderedQty || 0) - Number(line.fulfilledQty || 0));
      nextQuantities[String(line.product?._id || line.product)] = remaining;
    });
    setQuantities(nextQuantities);
    setAutoCreateInvoice(true);
  }, [open, order]);

  if (!open || !order) return null;

  return (
    <ModalShell title={`Fulfill ${order.orderNumber}`} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <p className="text-sm text-gray-600">
          Enter only the quantity actually collected or delivered. Fulfillment reduces on-hand stock and releases reserved stock for the same quantity.
        </p>
        {order.lines?.map((line) => {
          const productId = String(line.product?._id || line.product);
          const remaining = Math.max(0, Number(line.orderedQty || 0) - Number(line.fulfilledQty || 0));
          return (
            <div key={productId} className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-4 md:grid-cols-4">
              <div className="md:col-span-2">
                <p className="font-medium text-gray-900">{line.name}</p>
                <p className="text-sm text-gray-500">
                  Ordered {line.orderedQty} · Fulfilled {line.fulfilledQty || 0} · Reserved {line.reservedQty || 0}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Remaining</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{remaining}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Fulfill Now</label>
                <input
                  type="number"
                  min="0"
                  max={remaining}
                  step="1"
                  value={quantities[productId] ?? 0}
                  onChange={(event) =>
                    setQuantities((current) => ({ ...current, [productId]: event.target.value }))
                  }
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
            </div>
          );
        })}
        {canCreateInvoices && (
          <label className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <input
              type="checkbox"
              checked={autoCreateInvoice}
              onChange={(event) => setAutoCreateInvoice(event.target.checked)}
              className="h-4 w-4"
            />
            Auto-create B2B invoice when this fulfillment makes the order fully fulfilled.
          </label>
        )}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onSubmit({
                status: 'Fulfilled',
                fulfillmentLines: order.lines
                  .map((line) => ({
                    product: line.product?._id || line.product,
                    quantity: Number(quantities[String(line.product?._id || line.product)] || 0),
                  }))
                  .filter((line) => line.quantity > 0),
                autoCreateInvoice,
              })
            }
            className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark"
          >
            Fulfill Selected Quantity
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const InvoiceFromOrderModal = ({ open, order, error, onClose, onSubmit }) => {
  const [invoiceDate, setInvoiceDate] = useState(toApiDateValue(new Date()));
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setInvoiceDate(toApiDateValue(new Date()));
    setNotes(order?.notes || '');
  }, [open, order]);

  if (!open || !order) return null;

  return (
    <ModalShell title={`Create Invoice from ${order.orderNumber}`} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          The invoice will include only fulfilled quantities that have not already been invoiced.
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Invoice Date</label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Customer</label>
            <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {order.customer?.b2bProfile?.businessName ||
                `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim()}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            rows="3"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit({ invoiceDate, notes })}
            className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark"
          >
            Create Invoice
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const OrderDetailModal = ({ open, order, onClose }) => {
  const [linePage, setLinePage] = useState(1);
  const [linePageSize, setLinePageSize] = useState(10);

  useEffect(() => {
    if (open) {
      setLinePage(1);
    }
  }, [open, order?._id]);

  const lines = order?.lines || [];
  const totalLinePages = Math.max(1, Math.ceil(lines.length / linePageSize));
  const currentLinePage = Math.min(linePage, totalLinePages);
  const lineStartIndex = (currentLinePage - 1) * linePageSize;
  const pagedLines = lines.slice(lineStartIndex, lineStartIndex + linePageSize);
  const lineRangeStart = lines.length ? lineStartIndex + 1 : 0;
  const lineRangeEnd = lines.length ? lineStartIndex + pagedLines.length : 0;

  useEffect(() => {
    if (linePage !== currentLinePage) {
      setLinePage(currentLinePage);
    }
  }, [currentLinePage, linePage]);

  if (!open || !order) return null;

  return (
    <ModalShell title={`B2B Order ${order.orderNumber}`} onClose={onClose} wide>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-3 rounded-lg border border-gray-200 p-4 lg:col-span-1">
          <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
          <p><span className="font-medium">Customer:</span> {order.customer?.b2bProfile?.businessName || `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim()}</p>
          <p><span className="font-medium">Status:</span> {order.status}</p>
          <p><span className="font-medium">Fulfillment:</span> {order.fulfillmentType}</p>
          <p><span className="font-medium">Customer Ref:</span> {order.customerReference || 'None'}</p>
          <p><span className="font-medium">Requested:</span> {toDisplayDateValue(order.requestedDate) || 'Not set'}</p>
          <p><span className="font-medium">Expected:</span> {toDisplayDateValue(order.expectedFulfillmentDate) || 'Not set'}</p>
          <p><span className="font-medium">Grand Total:</span> {currency(order.grandTotal)}</p>
          <p><span className="font-medium">Notes:</span> {order.notes || 'None'}</p>
        </div>
        <div className="rounded-lg border border-gray-200 p-4 lg:col-span-2">
          <h3 className="mb-3 text-lg font-semibold text-gray-900">Lines</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Ordered</th>
                  <th className="px-4 py-2 text-right">Reserved</th>
                  <th className="px-4 py-2 text-right">Fulfilled</th>
                  <th className="px-4 py-2 text-right">Invoiced</th>
                  <th className="px-4 py-2 text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {pagedLines.map((line) => (
                  <tr key={line.product?._id || line.product}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{line.name}</div>
                      <div className="text-xs text-gray-500">{line.sku || line.barcode || 'No SKU/barcode'}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{line.orderedQty}</td>
                    <td className="px-4 py-2 text-right">{line.reservedQty || 0}</td>
                    <td className="px-4 py-2 text-right">{line.fulfilledQty || 0}</td>
                    <td className="px-4 py-2 text-right">{line.invoicedQty || 0}</td>
                    <td className="px-4 py-2 text-right">{currency(line.lineNetTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-col gap-2 border-t border-gray-200 pt-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Showing {lineRangeStart}-{lineRangeEnd} of {lines.length} line items
            </span>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Rows</label>
              <select
                value={linePageSize}
                onChange={(event) => {
                  setLinePageSize(Number(event.target.value || 10));
                  setLinePage(1);
                }}
                className="rounded-md border-gray-300 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                type="button"
                onClick={() => setLinePage((current) => Math.max(1, current - 1))}
                disabled={currentLinePage <= 1}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Prev
              </button>
              <span>
                Page {currentLinePage} / {totalLinePages}
              </span>
              <button
                type="button"
                onClick={() => setLinePage((current) => Math.min(totalLinePages, current + 1))}
                disabled={currentLinePage >= totalLinePages}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

const B2BOrdersPage = () => {
  const { activeStore } = useStore();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState(() => ({
    status: '',
    orderNumber: String(searchParams.get('orderNumber') || '').trim(),
  }));
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderModalError, setOrderModalError] = useState('');
  const [fulfillmentModalError, setFulfillmentModalError] = useState('');
  const [invoiceModalError, setInvoiceModalError] = useState('');
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [fulfillmentModalOpen, setFulfillmentModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const inlineEditorRef = useRef(null);

  const canRead = user?.permissions?.includes('b2b_orders:read');
  const canCreate = user?.permissions?.includes('b2b_orders:create');
  const canUpdate = user?.permissions?.includes('b2b_orders:update');
  const canFulfill = user?.permissions?.includes('b2b_orders:fulfill');
  const canCancel = user?.permissions?.includes('b2b_orders:cancel');
  const canCreateInvoices = user?.permissions?.includes('b2b_invoices:create');
  const canManageCustomers = user?.permissions?.includes('customers:manage');
  const activeStoreId = activeStore?._id;

  const loadOrders = useCallback(async (page = 1, limit = pageSize) => {
    if (!activeStoreId || !canRead) return;
    setLoading(true);
    setError('');
    try {
      const response = await b2bOrderService.fetchOrders({
        storeId: activeStoreId,
        status: filters.status || undefined,
        orderNumber: filters.orderNumber || undefined,
        page,
        limit,
      });
      setOrders(response.data || []);
      setPagination(response.pagination || { page, pages: 1, total: response.data?.length || 0, limit });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load B2B orders.');
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, canRead, filters.orderNumber, filters.status, pageSize]);

  useEffect(() => {
    if (!activeStoreId || !canRead) return;
    loadOrders(1);
  }, [activeStoreId, canRead, loadOrders]);

  useEffect(() => {
    if (!orderModalOpen) return;
    inlineEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [orderModalOpen]);

  const handleCustomerSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setCustomers([]);
      return;
    }
    setCustomersLoading(true);
    try {
      const results = await customerService.getCustomers(term.trim());
      setCustomers(results || []);
    } catch (err) {
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const handleCreateCustomer = useCallback(
    async (payload) => {
      if (!activeStoreId) {
        throw new Error('Select a store before creating a customer.');
      }
      if (!canManageCustomers) {
        throw new Error('You do not have permission to create customers.');
      }
      const createdCustomer = await customerService.createCustomer({
        ...payload,
        storeId: activeStoreId,
      });
      setCustomers((current) => {
        const existing = current.filter((customer) => String(customer._id) !== String(createdCustomer._id));
        return [createdCustomer, ...existing];
      });
      return createdCustomer;
    },
    [activeStoreId, canManageCustomers]
  );

  const openOrderModal = async (order = null, detail = false) => {
    setOrderModalError('');
    if (order?._id) {
      try {
        const detailedOrder = await b2bOrderService.getOrder(order._id);
        setSelectedOrder(detailedOrder);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load B2B order details.');
        return;
      }
    } else {
      setSelectedOrder(null);
      setCustomerSearch('');
      setCustomers([]);
    }

    if (detail) {
      setDetailModalOpen(true);
      return;
    }
    setOrderModalOpen(true);
  };

  const handleSaveOrder = async (payload) => {
    try {
      if (selectedOrder?._id) {
        const updatePayload = {
          status: payload.status,
          fulfillmentType: payload.fulfillmentType,
          customerReference: payload.customerReference,
          requestedDate: payload.requestedDate,
          expectedFulfillmentDate: payload.expectedFulfillmentDate,
          notes: payload.notes,
          orderDiscount: payload.orderDiscount,
          lines: payload.lines,
        };
        await b2bOrderService.updateOrder(selectedOrder._id, updatePayload);
      } else {
        await b2bOrderService.createOrder(payload);
      }
      setOrderModalOpen(false);
      setSelectedOrder(null);
      setOrderModalError('');
      loadOrders(pagination.page);
    } catch (err) {
      setOrderModalError(err.response?.data?.message || 'Unable to save B2B order.');
    }
  };

  const openFulfillment = async (order) => {
    try {
      const detailedOrder = await b2bOrderService.getOrder(order._id);
      setSelectedOrder(detailedOrder);
      setFulfillmentModalError('');
      setFulfillmentModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load order for fulfillment.');
    }
  };

  const handlePackOrder = async (orderId) => {
    try {
      await b2bOrderService.updateOrderStatus(orderId, { status: 'Packed' });
      loadOrders(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to pack order.');
    }
  };

  const handleFulfillOrder = async (payload) => {
    try {
      const { autoCreateInvoice, ...statusPayload } = payload || {};
      const updatedOrder = await b2bOrderService.updateOrderStatus(selectedOrder._id, statusPayload);

      if (autoCreateInvoice && canCreateInvoices && updatedOrder?.status === 'Fulfilled') {
        try {
          await b2bOrderService.createInvoiceFromOrder(updatedOrder._id, {
            invoiceDate: toApiDateValue(new Date()),
            notes: updatedOrder.notes || '',
          });
        } catch (invoiceErr) {
          setError(
            invoiceErr.response?.data?.message ||
              'Order was fulfilled, but invoice auto-creation failed. Use Create invoice action to retry.'
          );
        }
      }

      setFulfillmentModalOpen(false);
      setSelectedOrder(null);
      setFulfillmentModalError('');
      loadOrders(pagination.page);
    } catch (err) {
      setFulfillmentModalError(err.response?.data?.message || 'Unable to fulfill order.');
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Cancel this B2B order? Reservations will be released if allowed.')) return;
    try {
      await b2bOrderService.updateOrderStatus(orderId, { status: 'Cancelled' });
      loadOrders(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to cancel order.');
    }
  };

  const openInvoiceModal = async (order) => {
    try {
      const detailedOrder = await b2bOrderService.getOrder(order._id);
      setSelectedOrder(detailedOrder);
      setInvoiceModalError('');
      setInvoiceModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load order for invoicing.');
    }
  };

  const handleCreateInvoice = async (payload) => {
    try {
      await b2bOrderService.createInvoiceFromOrder(selectedOrder._id, payload);
      setInvoiceModalOpen(false);
      setSelectedOrder(null);
      setInvoiceModalError('');
      loadOrders(pagination.page);
    } catch (err) {
      setInvoiceModalError(err.response?.data?.message || 'Unable to create B2B invoice.');
    }
  };

  const orderRows = useMemo(() => orders || [], [orders]);
  const orderRangeStart = orderRows.length
    ? (Math.max(1, Number(pagination.page || 1)) - 1) * Number(pagination.limit || pageSize) + 1
    : 0;
  const orderRangeEnd = orderRows.length ? orderRangeStart + orderRows.length - 1 : 0;

  if (!canRead) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have permission to view B2B sales orders.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">B2B Sales Orders</h1>
          <p className="text-gray-500">Create commercial orders, reserve stock, fulfill handover, and raise B2B invoices.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadOrders(pagination.page)}
            className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            <FiRefreshCcw className="mr-2" />
            Refresh
          </button>
          {canCreate && (
            <button
              onClick={() => {
                if (orderModalOpen) {
                  setOrderModalOpen(false);
                  setSelectedOrder(null);
                  setOrderModalError('');
                  return;
                }
                openOrderModal(null);
              }}
              className="inline-flex items-center rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark"
            >
              <FiPlusCircle className="mr-2" />
              {orderModalOpen ? 'Close Editor' : 'New B2B Order'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}

      <div className="mb-6 grid grid-cols-1 gap-4 rounded-xl bg-white p-4 shadow md:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select
            value={filters.status}
            onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          >
            <option value="">All statuses</option>
            {Object.keys(statusBadgeClasses).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Order Number</label>
          <input
            type="text"
            value={filters.orderNumber}
            onChange={(event) => setFilters((current) => ({ ...current, orderNumber: event.target.value }))}
            placeholder="Search by order number"
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={() => loadOrders(1)}
            className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {orderModalOpen && (
        <div ref={inlineEditorRef} className="mb-6">
          <B2BOrderFormModal
            open={orderModalOpen}
            order={selectedOrder}
            activeStoreId={activeStoreId}
            customers={customers}
            customersLoading={customersLoading}
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            onCustomerSearch={handleCustomerSearch}
            canManageCustomers={canManageCustomers}
            onCreateCustomer={handleCreateCustomer}
            error={orderModalError}
            inline
            onClose={() => {
              setOrderModalOpen(false);
              setSelectedOrder(null);
              setOrderModalError('');
            }}
            onSave={handleSaveOrder}
          />
        </div>
      )}

      <div className="overflow-hidden rounded-xl bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Order</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Requested</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Expected</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Total</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {!loading && orderRows.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No B2B orders found for the selected filters.
                  </td>
                </tr>
              )}
              {orderRows.map((order) => (
                <tr key={order._id}>
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">{order.orderNumber}</div>
                    <div className="text-xs text-gray-500">{order.fulfillmentType}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">
                      {order.customer?.b2bProfile?.businessName ||
                        `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim()}
                    </div>
                    <div className="text-xs text-gray-500">{order.customer?.customerId || 'No customer ID'}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClasses[order.status] || 'bg-gray-100 text-gray-700'}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{toDisplayDateValue(order.requestedDate) || 'Not set'}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{toDisplayDateValue(order.expectedFulfillmentDate) || 'Not set'}</td>
                  <td className="px-4 py-4 text-right font-medium text-gray-900">{currency(order.grandTotal)}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        onClick={() => openOrderModal(order, true)}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        title="View"
                      >
                        <FiEye />
                      </button>
                      {canUpdate && ['Draft', 'Confirmed'].includes(order.status) && (
                        <button
                          onClick={() => openOrderModal(order)}
                          className="rounded-md border border-indigo-200 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-50"
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                      )}
                      {canFulfill && ['Confirmed', 'Packed', 'PartiallyFulfilled'].includes(order.status) && (
                        <button
                          onClick={() => handlePackOrder(order._id)}
                          disabled={order.status === 'Packed'}
                          className="rounded-md border border-amber-200 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Pack"
                        >
                          <FiPackage />
                        </button>
                      )}
                      {canFulfill && ['Packed', 'PartiallyFulfilled'].includes(order.status) && (
                        <button
                          onClick={() => openFulfillment(order)}
                          className="rounded-md border border-emerald-200 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
                          title="Fulfill"
                        >
                          <FiCheckCircle />
                        </button>
                      )}
                      {canCreateInvoices && ['PartiallyFulfilled', 'Fulfilled'].includes(order.status) && (
                        <button
                          onClick={() => openInvoiceModal(order)}
                          className="rounded-md border border-blue-200 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                          title="Create invoice"
                        >
                          <FiDollarSign />
                        </button>
                      )}
                      {canCancel && !['Cancelled', 'Fulfilled'].includes(order.status) && (
                        <button
                          onClick={() => handleCancelOrder(order._id)}
                          className="rounded-md border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                          title="Cancel"
                        >
                          <FiBox />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-3 rounded-xl bg-white px-4 py-3 shadow sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-gray-600">
          Showing {orderRangeStart}-{orderRangeEnd} of {pagination.total || 0} orders
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Rows</label>
          <select
            value={pageSize}
            onChange={(event) => {
              const nextLimit = Number(event.target.value || 25);
              setPageSize(nextLimit);
              loadOrders(1, nextLimit);
            }}
            className="rounded-md border-gray-300 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            type="button"
            onClick={() => loadOrders(Number(pagination.page || 1) - 1)}
            disabled={loading || Number(pagination.page || 1) <= 1}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {Math.max(1, Number(pagination.page || 1))} / {Math.max(1, Number(pagination.pages || 1))}
          </span>
          <button
            type="button"
            onClick={() => loadOrders(Number(pagination.page || 1) + 1)}
            disabled={loading || Number(pagination.page || 1) >= Number(pagination.pages || 1)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <OrderDetailModal
        open={detailModalOpen}
        order={selectedOrder}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedOrder(null);
        }}
      />

      <FulfillmentModal
        open={fulfillmentModalOpen}
        order={selectedOrder}
        error={fulfillmentModalError}
        canCreateInvoices={canCreateInvoices}
        onClose={() => {
          setFulfillmentModalOpen(false);
          setSelectedOrder(null);
          setFulfillmentModalError('');
        }}
        onSubmit={handleFulfillOrder}
      />

      <InvoiceFromOrderModal
        open={invoiceModalOpen}
        order={selectedOrder}
        error={invoiceModalError}
        onClose={() => {
          setInvoiceModalOpen(false);
          setSelectedOrder(null);
          setInvoiceModalError('');
        }}
        onSubmit={handleCreateInvoice}
      />
    </div>
  );
};

export default B2BOrdersPage;
